/**
 * 地图适配层：当前 Leaflet + OSM；日后可换成高德而不改业务页。
 * API: initMap / setMarkers / setClusters / drawRoute / fitBounds / destroy
 */
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// CRA/webpack 默认 marker 图标路径修复
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const OSM_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

export function initMap(container, options = {}) {
  const center = options.center || [31.2, 117.3];
  const zoom = options.zoom ?? 5;
  const map = L.map(container, {
    zoomControl: true,
    attributionControl: true,
  }).setView(center, zoom);

  L.tileLayer(OSM_URL, {
    maxZoom: 18,
    attribution: OSM_ATTR,
  }).addTo(map);

  const layers = {
    markers: L.layerGroup().addTo(map),
    clusters: L.layerGroup().addTo(map),
    route: L.layerGroup().addTo(map),
  };

  return {
    map,
    layers,
    provider: "leaflet",
  };
}

export function clearLayer(group) {
  if (group) group.clearLayers();
}

/**
 * markers: [{ lat, lng, title?, onClick? }]
 */
export function setMarkers(adapter, markers = []) {
  if (!adapter?.layers?.markers) return;
  clearLayer(adapter.layers.markers);
  markers.forEach((m) => {
    if (m.lat == null || m.lng == null) return;
    const marker = L.marker([m.lat, m.lng]);
    if (m.title) marker.bindPopup(m.title);
    if (typeof m.onClick === "function") {
      marker.on("click", () => m.onClick(m));
    }
    marker.addTo(adapter.layers.markers);
  });
}

/**
 * clusters: [{ lat, lng, label }]  — 小样本城市「有人」
 */
export function setClusters(adapter, clusters = []) {
  if (!adapter?.layers?.clusters) return;
  clearLayer(adapter.layers.clusters);
  clusters.forEach((c) => {
    if (c.lat == null || c.lng == null) return;
    const circle = L.circleMarker([c.lat, c.lng], {
      radius: 14,
      color: "#0f766e",
      fillColor: "#14b8a6",
      fillOpacity: 0.75,
      weight: 2,
    });
    circle.bindPopup(c.label || "有人");
    if (typeof c.onClick === "function") {
      circle.on("click", () => c.onClick(c));
    }
    circle.addTo(adapter.layers.clusters);
  });
}

/**
 * drawRoute: 静态折线 起点→终点
 */
export function drawRoute(adapter, points = [], options = {}) {
  if (!adapter?.layers?.route) return;
  clearLayer(adapter.layers.route);
  const valid = (points || []).filter((p) => p && p.lat != null && p.lng != null);
  if (valid.length < 2) return;
  const latlngs = valid.map((p) => [p.lat, p.lng]);
  const line = L.polyline(latlngs, {
    color: options.color || "#0d9488",
    weight: options.weight || 3,
    dashArray: options.dashArray || "8 6",
    opacity: 0.85,
  }).addTo(adapter.layers.route);
  L.circleMarker(latlngs[0], {
    radius: 7,
    color: "#115e59",
    fillColor: "#5eead4",
    fillOpacity: 1,
  }).addTo(adapter.layers.route);
  L.circleMarker(latlngs[latlngs.length - 1], {
    radius: 7,
    color: "#9f1239",
    fillColor: "#fb7185",
    fillOpacity: 1,
  }).addTo(adapter.layers.route);
  return line;
}

export function fitBounds(adapter, points = []) {
  if (!adapter?.map) return;
  const valid = (points || []).filter((p) => p && p.lat != null && p.lng != null);
  if (!valid.length) return;
  const bounds = L.latLngBounds(valid.map((p) => [p.lat, p.lng]));
  adapter.map.fitBounds(bounds.pad(0.2));
}

export function destroy(adapter) {
  if (adapter?.map) {
    adapter.map.remove();
  }
}

const mapAdapter = {
  initMap,
  setMarkers,
  setClusters,
  drawRoute,
  fitBounds,
  destroy,
  clearLayer,
};

export default mapAdapter;
